import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { noop } from 'lodash';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Icon, Image, Spinner } from 'components';
import ActionMenu from 'components/ActionMenu';
import { addCohostModalInstance } from 'components/AddCohostModal';
import Calendar from 'components/Calendar';
import { ButtonV3 } from 'components/ComponentV2';
import Confirmation from 'components/Confirmation';
import Modal from 'components/Modal';
import Tooltip from 'components/Tooltip';
import { Button } from 'components/form';
import { Row } from 'components/layout';
import { renderFeatureNotification } from 'components/layouts/Header/Header.component';

import {
  ConfirmationScreen,
  TabsType,
  eventColumns,
} from 'hooks/useCampaigns.hook';
import { useCohost } from 'hooks/useCohost';
import { useEvent } from 'hooks/useEvent';
import { useNotification } from 'hooks/useNotification';

import { useCompanyProfile } from 'hooks/UseCompanyProfile.hook';

import { useCampaigns } from 'lib/contexts/campaigns-context';
import { useSocietiesSearch } from 'lib/contexts/societies-search-context';
import { CohostResponse } from 'lib/models/cohost';
import {
  HorizontalPositionEnum,
  VerticalPositionEnum,
} from 'lib/models/notification';
import { TouchpointState } from 'lib/models/opportunity';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseResponse } from 'lib/utils/parser';

import { getRatingAverage } from 'utils/format';
import styles from './CompanyEventsTable.module.scss';

export const EventStateOption = [
  {
    id: '1',
    value: 'all',
    label: 'All',
  },
  {
    id: '2',
    value: 'published',
    label: 'Live',
  },
  {
    id: '3',
    value: 'ready_for_approval',
    label: 'In review',
  },
  {
    id: '4',
    value: 'draft',
    label: 'Drafts',
  },
  // todo: when co-host inviting is added for companies
  // {
  //   id: '5',
  //   value: 'Invited',
  //   label: 'Invited',
  // },
  {
    id: '6',
    value: 'expired',
    label: 'Ended',
  },
];

export interface CompanyEventsTableProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  mutateCompanyDashboard: () => void;
  companyId: string;
  companySlug: string;
}

export const CompanyEventsTable: React.FC<CompanyEventsTableProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    mutateCompanyDashboard,
    companyId,
    companySlug,
  }: CompanyEventsTableProps) => {
    const contentClassNames = cn(
      {
        [styles.CompanyEventsTable]: true,
      },
      className
    );
    const notificationInstance = useNotification();
    const router = useRouter();
    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const [touchpoint, setTouchpoint] = useState<{
      id: string;
      title: string;
      state: string;
    } | null>(null);
    const [isDeleteButtonLoading, setIsDeleteButtonLoading] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
      useState(false);
    const [confirmationScreenSelected, setConfirmationScreenSelected] =
      useState<ConfirmationScreen>(ConfirmationScreen.NO_SCREEN);

    const [featuredIds, setFeaturedIds] = useState<string[]>([]);

    const { setSearch } = useSocietiesSearch();

    const { addCohost } = useCohost();

    const { companyDashboard } = useCompanyProfile();
    const { event_status_count_mapping } = companyDashboard || {};

    const { eventStateChangeHandler, getState, shouldPublishEvent } =
      useEvent();
    const {
      eventsList,
      size,
      setSize,
      hasMoreRecords,
      isLoadingMore,
      totalCount,
      selectedEventState,
      setSelectedEventState,
      isAPILoading,
      onDelete,
      mutateEvents,
      onFeaturedToggle,
      getFeaturedIds,
    } = useCampaigns();

    const onUnpublishEventHandler = useCallback(async () => {
      try {
        if (touchpoint) {
          const response = await eventStateChangeHandler({
            state: touchpoint.state as TouchpointState,
            id: touchpoint.id as string,
            unpublish: true,
          });
          if (response) {
            mutateEvents();
            mutateCompanyDashboard();
            setIsConfirmationModalOpen(false);
            setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
            setTouchpoint(null);
            notificationInstance.success({
              title: 'Event Unpublished!',
              message: `${touchpoint.title} has been unpublished successfully.`,
            });
          }
        }
      } catch (error) {
        console.error(error);
        notificationInstance.handleExceptionError(error);
      }
    }, [touchpoint]);

    useEffect(() => {
      getFeaturedIds(companyId).then((data) => {
        setFeaturedIds(data);
      });
    }, [eventsList]);

    const deleteConfirmationScreenDesc = touchpoint ? (
      <div>
        <p>
          Are you sure you want to delete{' '}
          <span style={{ fontWeight: 700 }}>{touchpoint.title}</span>?
        </p>
        <br />
        <p>All of the data and details associated with it will be gone.</p>
        <br />
        {touchpoint.state === TouchpointState.EXPIRED ? null : (
          <p>
            <button
              style={{
                background: 'none',
                color: 'blue',
                border: 'none',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '15px',
              }}
              onClick={() => onUnpublishEventHandler()}
            >
              Unpublish
            </button>{' '}
            instead if you want to keep the data.
          </p>
        )}
      </div>
    ) : null;

    const unpublishConfirmationScreenDesc = touchpoint ? (
      <p>
        Are you sure you want to unpublish event{' '}
        <span style={{ fontWeight: 700 }}>{touchpoint.title}</span>?
      </p>
    ) : null;

    const ConfirmationScreenProps = [
      {
        id: ConfirmationScreen.DELETE,
        title: 'Delete Event',
        desc: deleteConfirmationScreenDesc,
        buttonContent: 'Yes, Delete',
        onCancel: () => {
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
        },
        onConfirm: async () => {
          if (touchpoint) {
            setIsDeleteButtonLoading(true);
            const response = await onDelete(touchpoint.id as string);
            if (response) {
              mutateCompanyDashboard();
              setIsConfirmationModalOpen(false);
              setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
              setTouchpoint(null);
            }
            setIsDeleteButtonLoading(false);
          }
        },
      },
      {
        id: ConfirmationScreen.UNPUBLISH,
        title: 'Unpublish Event',
        desc: unpublishConfirmationScreenDesc,
        buttonContent: 'Yes, Unpublish',
        onCancel: () => {
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
          setTouchpoint(null);
        },
        onConfirm: async () => {
          await onUnpublishEventHandler();
        },
      },
    ];

    const getConfirmationModal = () => {
      const screen = ConfirmationScreenProps.find(
        (item) => item.id === confirmationScreenSelected
      );
      if (screen) {
        const { title, desc, buttonContent, onCancel, onConfirm } = screen;
        return (
          <Modal
            isOpen={isConfirmationModalOpen}
            onRequestClose={() => setIsConfirmationModalOpen(false)}
            isFullScreen={false}
          >
            <Confirmation
              title={title}
              desc={desc || ''}
              buttonContent={buttonContent}
              onCancel={onCancel}
              isLoading={isDeleteButtonLoading}
              onConfirm={onConfirm}
            />
          </Modal>
        );
      }
    };

    const onAddCohostClick = async ({
      companyId,
      touchpointId,
    }: {
      companyId: string;
      touchpointId: string;
    }) => {
      try {
        const cohostIds = await addCohostModalInstance({
          idsForCohost: { creatableForId: companyId, touchpointId },
        });
        if (cohostIds.length > 0) {
          for (let i = 0; i < cohostIds.length; i++) {
            const response = await addCohost(touchpointId, cohostIds[i]);
            if (!response) {
              break;
            } else {
              mutateEvents();
              notificationInstance.success({
                title: 'Invitation/s sent!',
                message: `Co-hosting invitation/s have been sent successfully.`,
              });
            }
          }
        }
        setSearch('');
      } catch (error) {
        noop;
      }
    };

    const getActionMenu = (
      id: string,
      title: string,
      state: string,
      applications: number,
      companyId: string,
      cohost_application: CohostResponse | null | undefined
    ) => {
      return [
        {
          id: '1',
          label: 'View',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            router.push(`${PAGE_ROUTES.CAMPAIGNS}/${id}`);
          },
        },
        {
          id: '2',
          label:
            state === TouchpointState.PUBLISHED && applications > 0
              ? 'View attendees'
              : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            router.push(`${PAGE_ROUTES.CAMPAIGNS}/${id}/attendees`);
          },
        },
        {
          id: '3',
          label: state !== TouchpointState.EXPIRED ? 'Edit' : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const pathname = `${PAGE_ROUTES.CAMPAIGNS_EDIT.replace(
              '[id]',
              id
            )}?step=1&type=${TouchpointType.Event}`;
            router.push(pathname);
          },
        },
        {
          id: '4',
          label:
            cohost_application === null && state !== TouchpointState.EXPIRED
              ? 'Add Co-host'
              : null,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            onAddCohostClick({ companyId, touchpointId: id });
          },
        },
        {
          id: '5',
          label: state === TouchpointState.DRAFT ? 'Publish' : 'Unpublish',
          onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setTouchpoint({ id, title, state });
            if (state !== TouchpointState.DRAFT) {
              setIsConfirmationModalOpen(true);
              setConfirmationScreenSelected(ConfirmationScreen.UNPUBLISH);
            } else {
              const isEventValid = await shouldPublishEvent(id as string);
              if (!isEventValid) {
                const pathname = `${PAGE_ROUTES.CAMPAIGNS_EDIT.replace(
                  '[id]',
                  id
                )}?step=1&type=${TouchpointType.Event}`;
                notificationInstance.warning({
                  title: 'Required',
                  message: `Fill required information to publish event.`,
                });
                router.push(pathname);
              } else {
                try {
                  const response = await eventStateChangeHandler({
                    state: state as TouchpointState,
                    id: id as string,
                  });
                  if (response) {
                    mutateEvents();
                    notificationInstance.success({
                      title: 'Event Published!',
                      message: `${title} has been published successfully.`,
                    });
                  }
                } catch (error) {
                  console.error(error);
                  notificationInstance.handleExceptionError(error);
                }
              }
            }
          },
        },
        {
          id: '6',
          label: 'Delete',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            setTouchpoint({ id, title, state });
            setIsConfirmationModalOpen(true);
            setConfirmationScreenSelected(ConfirmationScreen.DELETE);
          },
        },
      ];
    };

    const [loading, setLoading] = useState(false);

    const eventData = useMemo(() => {
      if (!isAPILoading && eventsList && eventsList.length === 0) {
        return (
          <div className={styles.noResults}>
            <div className={styles.noResultsContent}>
              <div className={styles.noResultsContentText}>
                <Image src="desert.png" source="aws" height="auto" width="98" />
                <p>Post your job or internship</p>
              </div>
              <ButtonV3
                onClick={() =>
                  router.push(`${PAGE_ROUTES.CAMPAIGNS_CREATE}?step=1`)
                }
              >
                Start Hiring
              </ButtonV3>
            </div>
          </div>
        );
      } else {
        return (
          <table className={cn({ [styles.loading]: isLoadingMore })}>
            <thead>
              <tr>
                {eventColumns.map((column) => (
                  <th key={column.id} style={{ width: column.width }}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {eventsList.map((row) => {
                const {
                  id,
                  title,
                  start_date,
                  applications,
                  state,
                  view_counter,
                  cohost_application,
                  is_online,
                  online_link,
                  place: placeResponse,
                  rating_map,
                  creatable_for,
                  apply_url,
                } = row;
                const place = parseResponse(placeResponse);
                const ratingAverage = getRatingAverage(rating_map);
                const { id: companyId } = parseResponse(creatable_for);

                const opportunityState = getState(
                  state as TouchpointState,
                  cohost_application
                );

                const isFeatureDisabled =
                  featuredIds.length >= 3 || opportunityState !== 'Live';

                const featured = featuredIds.includes(id);

                return (
                  <tr
                    key={id}
                    className={cn({
                      [styles.invitationPending]:
                        getState(
                          state as TouchpointState,
                          cohost_application
                        ) === TouchpointState.INVITATION_PENDING,
                    })}
                    onClick={() =>
                      router.push(
                        {
                          pathname: `${PAGE_ROUTES.CAMPAIGNS}/${id}`,
                          query: { selectedEventState },
                        },
                        `${PAGE_ROUTES.CAMPAIGNS}/${id}`
                      )
                    }
                  >
                    <td>
                      {start_date && (
                        <Calendar
                          startDate={start_date}
                          className={cn({
                            [styles.expired]: state === TouchpointState.EXPIRED,
                          })}
                        />
                      )}
                    </td>
                    <td>
                      <div className={cn(styles.title, styles.titleSection)}>
                        <p>{title}</p>
                        {apply_url ? (
                          <Tooltip
                            content={
                              "This job is external. Enable easy apply to simplify the process, boost your job's visibility and get more applications."
                            }
                            position="top"
                            xAlign="center"
                            color="dark"
                            className={styles.externalLabelTooltip}
                            containerClassName={styles.titleSubSection}
                          >
                            External
                          </Tooltip>
                        ) : null}
                        {is_online && !online_link ? (
                          <p className={styles.titleSubSection}>
                            Meeting link missing
                          </p>
                        ) : null}
                        {!is_online && !place ? (
                          <p className={styles.titleSubSection}>
                            Location missing
                          </p>
                        ) : null}
                        {ratingAverage > 0 ? (
                          <p className={styles.rating}>⭐️ {ratingAverage}</p>
                        ) : null}
                      </div>
                    </td>
                    <td className={styles.state}>
                      {getState(state as TouchpointState, cohost_application)}
                    </td>
                    <td>{view_counter}</td>
                    <td>{applications}</td>
                    <td>
                      <div className={styles.actionButtonWrapper}>
                        {opportunityState === 'Live' ? (
                          featured ? (
                            <button
                              className={styles.featuredButton}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setLoading(true);
                                onFeaturedToggle({
                                  id,
                                  featured: !featured,
                                  companySlug,
                                })
                                  .then((res) => {
                                    notificationInstance.custom({
                                      message: '',
                                      timeout: 2500,
                                      position: {
                                        vertical: VerticalPositionEnum.TOP,
                                        horizontal:
                                          HorizontalPositionEnum.RIGHT,
                                      },
                                      renderContent: (
                                        fn?: (() => void) | undefined
                                      ) =>
                                        renderFeatureNotification(
                                          PAGE_ROUTES.COMPANY_PUBLIC.replace(
                                            '[slug]',
                                            companySlug
                                          ),
                                          'Event has been removed from featured!',
                                          fn
                                        ),
                                    });
                                    if (res) {
                                      const newIds = [...featuredIds];
                                      const index = newIds.indexOf(id);
                                      newIds.splice(index, 1);
                                      setFeaturedIds(newIds);
                                      mutateEvents();
                                    }
                                  })
                                  .finally(() => {
                                    setLoading(false);
                                  });
                              }}
                            >
                              <Icon
                                iconName="icon_star"
                                size="small"
                                className={styles.starIcon}
                              />
                            </button>
                          ) : (
                            <Tooltip
                              content={
                                isFeatureDisabled
                                  ? 'You can feature up to 3 opportunities'
                                  : 'Feature'
                              }
                              position="top"
                              xAlign="center"
                              color="dark"
                              className={cn(styles.featuredTooltip, {
                                [styles.featuredTooltipWidthAuto]:
                                  !isFeatureDisabled,
                              })}
                              containerClassName={
                                styles.featuredTooltipContainer
                              }
                            >
                              <button
                                className={cn(
                                  styles.featuredButton,
                                  styles.notFeatured
                                )}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setLoading(true);

                                  onFeaturedToggle({
                                    id,
                                    featured: !featured,
                                    companySlug,
                                  })
                                    .then((res) => {
                                      notificationInstance.custom({
                                        message: '',
                                        timeout: 2500,
                                        position: {
                                          vertical: VerticalPositionEnum.TOP,
                                          horizontal:
                                            HorizontalPositionEnum.RIGHT,
                                        },
                                        renderContent: (
                                          fn?: (() => void) | undefined
                                        ) =>
                                          renderFeatureNotification(
                                            PAGE_ROUTES.COMPANY_PUBLIC.replace(
                                              '[slug]',
                                              companySlug
                                            ),
                                            'Event has been featured!',
                                            fn
                                          ),
                                      });
                                      if (res) {
                                        const newIds = [...featuredIds];
                                        newIds.push(id);
                                        setFeaturedIds(newIds);
                                        mutateEvents();
                                      }
                                    })
                                    .finally(() => {
                                      setLoading(false);
                                    });
                                }}
                                disabled={isFeatureDisabled}
                              >
                                <Icon
                                  iconName={'icon_star-outline'}
                                  size="small"
                                />
                              </button>
                            </Tooltip>
                          )
                        ) : (
                          <span
                            className={styles.featuredButtonPlaceholder}
                          ></span>
                        )}
                        <ActionMenu
                          options={getActionMenu(
                            id,
                            title,
                            state,
                            applications,
                            companyId,
                            cohost_application
                          )}
                          triggerButtonClassName={styles.actionmenu}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      }
    }, [selectedEventState, eventsList]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);
      if (router.query && router.query.selectedEventState !== undefined) {
        const index = Number(router.query.selectedEventState);
        setSelectedEventState(index);
      }

      return () => {
        endOfPageRef.current = null;
        setSelectedEventState(TabsType.ALL);
      };
    }, []);

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="CompanyEventsTable"
      >
        {loading && <Spinner showBackDrop={true} />}
        <Tab.Group
          selectedIndex={selectedEventState}
          onChange={(index) => {
            setSelectedEventState(index);
            setSize(1);
          }}
        >
          <Tab.List className={styles.tabList}>
            {EventStateOption.map((tab) => {
              const isValuePresent = event_status_count_mapping
                ? event_status_count_mapping[
                    tab.value as keyof typeof event_status_count_mapping
                  ]
                : false;
              return (
                <Tab
                  key={tab.id}
                  className={({ selected }) =>
                    cn(
                      styles.tab,
                      { [styles.tabSelected]: selected },
                      {
                        [styles.tabDisabled]: !isValuePresent && tab.id !== '1',
                      }
                    )
                  }
                >
                  {tab.label}
                </Tab>
              );
            })}
          </Tab.List>
          <Tab.Panels className={styles.tabPanels}>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
            <Tab.Panel>{eventData}</Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
        {!isAPILoading && isLoadingMore ? (
          <Spinner size="small" className={styles.spinner} />
        ) : null}
        <Row align="center" className={styles.countsRow}>
          {eventsList?.length ? (
            <p>
              Showing {eventsList?.length} of {totalCount}
            </p>
          ) : null}
          {hasMoreRecords ? (
            <Button
              variant="text"
              onClick={() => {
                setSize(size + 1);
              }}
              className={styles.countsButton}
            >
              Show more
            </Button>
          ) : null}
          <div ref={endOfPageRef}></div>
        </Row>
        {isConfirmationModalOpen ? getConfirmationModal() : null}
      </div>
    );
  }
);

CompanyEventsTable.displayName = 'CompanyEventsTable';
